/**
 * DataTable helper sub-components.
 * Extracted here to keep data-table.tsx within the max-lines-per-function lint budget.
 */
import * as React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { TableHead } from './table.js';
import { Checkbox } from './checkbox.js';
import { alignClass, resolveHeaderStyle } from './data-table-hooks.js';
import type { ColumnDef } from './data-table.js';

const TABLE_HEADER_SCOPE = 'col';
export const SELECTION_COL_WIDTH = 40;

// ─── Row-link (stretched-link) helpers ─────────────────────────
// Implements the "stretched link" pattern for whole-row navigation instead of a
// `<tr onClick>` handler (see Adrian Roselli's "Block Links, Cards, Clickable Regions,
// Rows, Etc." — the table-specific recipe, not Bootstrap's own `.stretched-link`, which
// explicitly warns it doesn't work reliably on table elements). A real, consumer-supplied
// anchor (e.g. a router `Link`) lives in a dedicated zero-width structural first column
// (see ROW_LINK_COL_WIDTH below) — it renders no visible content of its own (see
// `RowDetailLink`'s docblock in apps/web), so the column collapses to zero width instead
// of leaving a dead, invisible-but-clickable strip next to the real first column. Its
// `::after` pseudo-element is `position: absolute; inset: 0`, which — because DataTable
// gives the row (`<tr>`) `position: relative` whenever `renderRowLink` is set — visually
// stretches to cover the ENTIRE row's hit-test area, not just this one cell.
//
// z-index scheme (documented once here, applied at both ends):
// - The overlay pseudo-element sits at z-0 (`ROW_LINK_ANCHOR_CLASSNAME`).
// - Any OTHER per-row interactive cell (checkboxes, popover triggers, the availability
//   "load more" chevron) that must win a click/drag over the overlay gets
//   `INTERACTIVE_CELL_CLASSNAME` (`relative z-[1]`) — one level above the overlay, so the
//   browser's paint-order-based hit-testing picks the interactive cell first regardless of
//   its DOM position relative to the row-link column.
export const ROW_LINK_COL_WIDTH = 0;

export const ROW_LINK_ANCHOR_CLASSNAME =
  // flex/h-full/w-full give the anchor a real block-level box spanning its cell (needed
  // for the h-full/w-full percentages to resolve at all — a bare <a> is inline, and
  // percentage sizing doesn't apply to inline elements). items-center/justify-center and
  // the text-color/hover/transition classes were dropped: the anchor never renders visible
  // children (see the column comment above), so there is nothing to center and no color to
  // transition.
  'flex h-full w-full focus-visible:outline-none ' +
  // The column is zero-width, so a ring drawn on the anchor's OWN box would be invisible.
  // Put it on the `::after` overlay instead — already stretched to cover the whole row
  // above — so tabbing to a row's link draws a ring around the WHOLE ROW. That's an
  // improvement over the old cell-sized ring anyway, since the link's actual activation
  // area was always the whole row.
  'focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset ' +
  // Intentionally NOT position:relative on the anchor itself — that would trap the
  // pseudo-element's `inset-0` inside this zero-width cell instead of letting it escape up
  // to the `position: relative` `<tr>` ancestor DataTable sets.
  'after:absolute after:inset-0 after:z-0 after:content-[""]';

export const INTERACTIVE_CELL_CLASSNAME = 'relative z-[1]';

interface RowLinkHeaderCellProps {
  /** See `ColumnHeaderCellProps.measurementOnly`. */
  measurementOnly?: boolean;
}

/** Empty structural header cell for the row-link column — purely structural, so it
 * carries no visible label or accessible name of its own; each row's anchor supplies
 * its own accessible name via `renderRowLink`. */
export function RowLinkHeaderCell({ measurementOnly = false }: RowLinkHeaderCellProps = {}) {
  return (
    <TableHead
      scope={TABLE_HEADER_SCOPE}
      style={{
        width: ROW_LINK_COL_WIDTH,
        minWidth: ROW_LINK_COL_WIDTH,
        maxWidth: ROW_LINK_COL_WIDTH,
        position: 'relative',
      }}
      className="p-0"
      data-testid={measurementOnly ? undefined : 'row-link-header'}
      aria-hidden={measurementOnly || undefined}
    />
  );
}

interface RowLinkCellProps {
  children: React.ReactNode;
}

/** Structural first `<td>` hosting the per-row stretched-link anchor from `renderRowLink`. */
export function RowLinkCell({ children }: RowLinkCellProps) {
  return (
    <td
      style={{
        width: ROW_LINK_COL_WIDTH,
        minWidth: ROW_LINK_COL_WIDTH,
        maxWidth: ROW_LINK_COL_WIDTH,
      }}
      className="p-0"
      data-testid="row-link-cell"
    >
      {children}
    </td>
  );
}

// ─── Selection column helpers ─────────────────────────────────

export function computeHeaderCheckState(
  selectedCount: number,
  totalRows: number,
): boolean | 'indeterminate' {
  if (totalRows === 0 || selectedCount === 0) return false;
  if (selectedCount === totalRows) return true;
  return 'indeterminate';
}

function countSelected(ids: string[], selectedIds: Set<string | number>): number {
  return ids.filter((id) => selectedIds.has(id)).length;
}

export interface RowSelectionConfig {
  selectedIds: Set<string | number>;
  onChange: (newIds: Set<string | number>) => void;
  totalMatching?: number;
}

export interface RowSelectionState<T> {
  headerCheckState: boolean | 'indeterminate';
  handleHeaderToggle: () => void;
  renderSelectionCell: ((item: T) => React.ReactNode) | undefined;
}

/**
 * Derives all row-selection render state (header checkbox state, header toggle handler, and
 * a per-row selection-cell renderer) from a `rowSelection` config. Extracted out of
 * `DataTable`'s own render body — a plain `if (!rowSelection)` here has its own complexity
 * budget instead of adding branches to DataTable's already-large render function.
 */
export function useRowSelectionState<T>(
  rowSelection: RowSelectionConfig | undefined,
  data: T[],
  getRowKey: (item: T) => string,
): RowSelectionState<T> {
  const allRowIds = rowSelection ? data.map((item) => getRowKey(item)) : [];
  const selectedCount = rowSelection ? countSelected(allRowIds, rowSelection.selectedIds) : 0;
  const headerCheckState = computeHeaderCheckState(selectedCount, data.length);

  const handleHeaderToggle = React.useCallback(() => {
    if (!rowSelection) return;
    const next = new Set(rowSelection.selectedIds);
    const op = selectedCount === data.length ? 'delete' : 'add';
    for (const id of allRowIds) next[op](id);
    rowSelection.onChange(next);
  }, [rowSelection, selectedCount, data.length, allRowIds]);

  const handleRowToggle = React.useCallback(
    (rowId: string) => {
      if (!rowSelection) return;
      const next = new Set(rowSelection.selectedIds);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      rowSelection.onChange(next);
    },
    [rowSelection],
  );

  const renderSelectionCell = rowSelection
    ? (item: T) => (
        <SelectionRowCell
          isSelected={rowSelection.selectedIds.has(getRowKey(item))}
          rowKey={getRowKey(item)}
          onToggle={() => handleRowToggle(getRowKey(item))}
        />
      )
    : undefined;

  return { headerCheckState, handleHeaderToggle, renderSelectionCell };
}

interface SelectionHeaderCellProps {
  checkState: boolean | 'indeterminate';
  onToggle: () => void;
  /** See `ColumnHeaderCellProps.measurementOnly`. */
  measurementOnly?: boolean;
}

export function SelectionHeaderCell({
  checkState,
  onToggle,
  measurementOnly = false,
}: SelectionHeaderCellProps) {
  return (
    <TableHead
      scope={TABLE_HEADER_SCOPE}
      style={{
        width: SELECTION_COL_WIDTH,
        minWidth: SELECTION_COL_WIDTH,
        maxWidth: SELECTION_COL_WIDTH,
        position: 'relative',
      }}
      className="text-center select-none"
      data-testid={measurementOnly ? undefined : 'selection-header'}
      aria-hidden={measurementOnly || undefined}
    >
      {measurementOnly ? null : (
        <Checkbox
          checked={checkState}
          onCheckedChange={onToggle}
          data-testid="select-all-checkbox"
        />
      )}
    </TableHead>
  );
}

interface SelectionRowCellProps {
  isSelected: boolean;
  rowKey: string;
  onToggle: () => void;
}

export function SelectionRowCell({ isSelected, rowKey, onToggle }: SelectionRowCellProps) {
  return (
    <td
      style={{
        width: SELECTION_COL_WIDTH,
        minWidth: SELECTION_COL_WIDTH,
        maxWidth: SELECTION_COL_WIDTH,
      }}
      // INTERACTIVE_CELL_CLASSNAME ('relative z-[1]') so this checkbox keeps winning
      // hit-tests over the row-link stretched-link overlay when `renderRowLink` is also
      // set — same mechanism as availability-rich-cell.tsx / media-units columns.tsx.
      className={`px-2 py-3 text-center whitespace-nowrap ${INTERACTIVE_CELL_CLASSNAME}`}
      data-testid={`row-checkbox-cell-${rowKey}`}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        data-testid={`row-checkbox-${rowKey}`}
      />
    </td>
  );
}

// ─── Resize handle ─────────────────────────────────────────────

interface ResizeHandleProps {
  colId: string;
  onMouseDown: (e: React.MouseEvent) => void;
}

export function ResizeHandle({ colId, onMouseDown }: ResizeHandleProps) {
  return (
    <div
      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 z-10"
      data-testid={`resize-handle-${colId}`}
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// ─── measurementOnly test-id suppression ────────────────────────

/** `undefined` for the invisible measurement-only header copy, `id` otherwise — see `ColumnHeaderCellProps.measurementOnly`. */
function measurementTestId(measurementOnly: boolean, id: string): string | undefined {
  return measurementOnly ? undefined : id;
}

// ─── Sort indicator ────────────────────────────────────────────

interface SortOrder {
  key: string;
  order: number;
  dir: 'asc' | 'desc';
}

interface SortIndicatorProps {
  sortable?: boolean;
  sortKey?: string;
  sortOrders?: SortOrder[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** See `ColumnHeaderCellProps.measurementOnly`. */
  measurementOnly?: boolean;
}

export function SortIndicator({
  sortable,
  sortKey,
  sortOrders,
  sortBy,
  sortDir,
  measurementOnly = false,
}: SortIndicatorProps) {
  if (!sortable || !sortKey) return null;
  if (sortOrders) {
    const match = sortOrders.find((s) => s.key === sortKey);
    if (match) {
      return (
        <span
          className="inline-flex items-center justify-center rounded bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
          data-testid={measurementTestId(measurementOnly, `sort-badge-${sortKey}`)}
        >
          {match.order}
          {match.dir === 'asc' ? '↑' : '↓'}
        </span>
      );
    }
    return null;
  }
  if (sortBy === sortKey) {
    return sortDir === 'asc' ? (
      <ChevronUp
        className="h-3 w-3 shrink-0"
        data-testid={measurementTestId(measurementOnly, 'sort-asc')}
      />
    ) : (
      <ChevronDown
        className="h-3 w-3 shrink-0"
        data-testid={measurementTestId(measurementOnly, 'sort-desc')}
      />
    );
  }
  return null;
}

// ─── Column header cell ───────────────────────────────────────

interface ColumnHeaderCellProps<T> {
  col: ColumnDef<T>;
  width: number | undefined;
  isResizing: boolean;
  isDragged: boolean;
  isDraggedOver: boolean;
  /** True when the table's `onColumnReorder` callback is provided — gates the drag affordance. */
  reorderEnabled: boolean;
  showResizeHandle: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onResizeStart: (e: React.MouseEvent) => void;
  onSortClick: ((e: React.MouseEvent) => void) | undefined;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  sortOrders?: SortOrder[];
  /**
   * True for the invisible width-measurement-only mirror of this header cell, rendered
   * inside the real scrolling `<table>` purely so the browser's own layout algorithm sees
   * identical content — and thus computes identical natural column widths — to the frozen,
   * visible, interactive header DataTable renders in its own sticky box outside the
   * horizontal-scroll wrapper (see `stickyHeaderOffsetPx` in data-table.tsx). Suppresses
   * test ids and interactive handlers so this row is never seen, focused, or matched by a
   * `getByTestId` query — only the frozen copy is.
   */
  measurementOnly?: boolean;
}

/**
 * Computes the header `<th>` className from sort/drag/resize state. Drag-affordance
 * classes (`cursor-grab`, drag/drop-target highlight) only apply when reorder is enabled.
 *
 * TableHead itself now defaults to `whitespace-normal break-words` (see table.tsx), so
 * this doesn't need to repeat that override. Width-driven `overflow-hidden` is kept: the
 * `<th>` has no fixed height, so a wrapped (taller) label just grows the header row
 * instead of getting clipped vertically. `col.headerClassName` is still applied last so a
 * per-column override still wins.
 */
function resolveHeaderClassName<T>(
  col: ColumnDef<T>,
  width: number | undefined,
  isDragged: boolean,
  isDraggedOver: boolean,
  reorderEnabled: boolean,
): string {
  return cn(
    alignClass(col.align),
    'select-none',
    width && 'overflow-hidden',
    col.sortable ? 'cursor-pointer hover:text-foreground' : reorderEnabled && 'cursor-grab',
    isDragged && reorderEnabled && 'opacity-50',
    isDraggedOver && reorderEnabled && 'border-l-2 border-primary',
    col.headerClassName,
  );
}

interface ColumnHeaderInteractionProps {
  draggable: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: () => void;
  onDragEnd?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  'aria-hidden'?: true;
  showResizeHandle: boolean;
}

/**
 * Resolves every interactive prop the measurement-only (invisible) header copy must
 * drop — pulled out of `ColumnHeaderCell`'s own body (own complexity scope) since each
 * "suppress this when measurementOnly" branch counts directly against that function's
 * cyclomatic complexity budget.
 */
function resolveColumnHeaderInteractionProps(
  measurementOnly: boolean,
  isResizing: boolean,
  fixed: boolean | undefined,
  reorderEnabled: boolean,
  requestedShowResizeHandle: boolean,
  handlers: {
    onDragStart: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: () => void;
    onDragEnd: () => void;
    onSortClick: ((e: React.MouseEvent) => void) | undefined;
  },
): ColumnHeaderInteractionProps {
  if (measurementOnly) {
    return { draggable: false, 'aria-hidden': true, showResizeHandle: false };
  }
  return {
    draggable: !isResizing && !fixed && reorderEnabled,
    onDragStart: handlers.onDragStart,
    onDragOver: handlers.onDragOver,
    onDrop: handlers.onDrop,
    onDragEnd: handlers.onDragEnd,
    onClick: handlers.onSortClick,
    showResizeHandle: requestedShowResizeHandle,
  };
}

/** A single sortable/resizable/draggable column header `<th>`. */
export function ColumnHeaderCell<T>({
  col,
  width,
  isResizing,
  isDragged,
  isDraggedOver,
  reorderEnabled,
  showResizeHandle,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onResizeStart,
  onSortClick,
  sortBy,
  sortDir,
  sortOrders,
  measurementOnly = false,
}: ColumnHeaderCellProps<T>) {
  const renderedHeader = col.renderHeader?.();
  const { showResizeHandle: resolvedShowResizeHandle, ...domInteractionProps } =
    resolveColumnHeaderInteractionProps(
      measurementOnly,
      isResizing,
      col.fixed,
      reorderEnabled,
      showResizeHandle,
      { onDragStart, onDragOver, onDrop, onDragEnd, onSortClick },
    );

  return (
    <TableHead
      scope={TABLE_HEADER_SCOPE}
      /*
       * load-bearing: read by snapshotThWidths in data-table-hooks.ts for column resize
       * AND for frozen-header width measurement. `measurementOnly` (the invisible,
       * in-table spacer copy rendered alongside a frozen header — see
       * `resolveUseFrozenHeader`) gets `data-measure-col-id` instead of `data-col-id`:
       * e2e specs select real, clickable header cells via `th[data-col-id="..."]`, and
       * with both copies carrying `data-col-id` that selector matched two elements
       * (the spacer is `aria-hidden`, which hides it from role/a11y queries but not
       * from a raw attribute selector). `snapshotThWidths` reads whichever attribute is
       * present, so it still finds this copy inside `theadRef` (the real thead).
       */
      {...(measurementOnly ? { 'data-measure-col-id': col.id } : { 'data-col-id': col.id })}
      style={resolveHeaderStyle(width)}
      className={resolveHeaderClassName(col, width, isDragged, isDraggedOver, reorderEnabled)}
      {...domInteractionProps}
    >
      <span className="flex items-center gap-1">
        {renderedHeader != null ? (
          renderedHeader
        ) : (
          // Plain-label case only: a bare text node here is an anonymous flex item,
          // which defaults to `min-width: auto` (min-content) and silently defeats
          // `whitespace-normal break-words` on the <th>, clipping long labels
          // mid-letter instead of wrapping. `min-w-0` lifts that floor so the item
          // can actually shrink and wrap. Custom `renderHeader` content keeps full
          // ownership of its own layout and is never wrapped here.
          <span className="min-w-0 break-words">{col.label}</span>
        )}
        <SortIndicator
          sortable={col.sortable}
          sortKey={col.sortKey}
          sortOrders={sortOrders}
          sortBy={sortBy}
          sortDir={sortDir}
          measurementOnly={measurementOnly}
        />
      </span>
      {resolvedShowResizeHandle ? (
        <ResizeHandle colId={col.id} onMouseDown={onResizeStart} />
      ) : null}
    </TableHead>
  );
}

// ─── Virtualizer sentinel ──────────────────────────────────────

interface TableVirtualizerSentinelProps {
  sentinelRef?: React.RefObject<HTMLDivElement | null>;
}

/** Renders the infinite-scroll sentinel div only when a virtualizer bundle is present. */
export function TableVirtualizerSentinel({ sentinelRef }: TableVirtualizerSentinelProps) {
  if (!sentinelRef) return null;
  return <div ref={sentinelRef} className="h-1" data-testid="table-virtualizer-sentinel" />;
}

// ─── Scroll shadow indicators ──────────────────────────────────

interface ScrollIndicatorsProps {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

/** Left/right gradient overlays shown when the table's scroll container has horizontal overflow. */
export function ScrollIndicators({ canScrollLeft, canScrollRight }: ScrollIndicatorsProps) {
  return (
    <>
      {canScrollLeft ? (
        <div
          className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10"
          data-testid="scroll-indicator-left"
        />
      ) : null}
      {canScrollRight ? (
        <div
          className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10"
          data-testid="scroll-indicator-right"
        />
      ) : null}
    </>
  );
}
